import React from 'react';
import { BadgeProps } from '@frontend/shadcn/components/ui/badge';
import { Tooltip, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';

import { CreatorProfileIcon } from '@/components/icons/creator-profile-icon';

import { BadgeDismissButton, BadgeTooltip, BaseBadgeWrapper, DemographicData } from './badge-parts';

interface UsernameBadgeProps {
  usernames: string[];
  onDismissItem?: (itemName: string) => void;
}

type CombinedProps = UsernameBadgeProps & BadgeProps;

interface SingleUsernameBadgeProps {
  username: string;
  index: number;
  isMultiple: boolean;
  data: DemographicData;
  usernames: string[];
  onDismissItem?: (itemName: string) => void;
  badgeProps: BadgeProps;
  forwardedRef?: React.Ref<HTMLDivElement>;
}

const SingleUsernameBadge: React.FC<SingleUsernameBadgeProps> = ({
  username,
  index,
  isMultiple,
  data,
  usernames,
  onDismissItem,
  badgeProps,
  forwardedRef,
}) => {
  const formatUsernames = (names: string[]) => names.join(', ');

  return (
    <Tooltip key={isMultiple ? `${data.id}-${username}-${index}` : data.id}>
      <TooltipTrigger asChild>
        <div className={isMultiple ? 'inline-block' : undefined}>
          <BaseBadgeWrapper
            ref={!isMultiple || index === 0 ? forwardedRef : undefined}
            id={isMultiple ? `${data.id}-${username}` : data.id}
            actionName="open-username-filters"
            {...badgeProps}
          >
            <CreatorProfileIcon className="mr-2 text-[#55A2DF]" />
            Username:<span className="ml-1">@{username}</span>
            <BadgeDismissButton
              onDismiss={(e) => {
                e.stopPropagation();
                if (onDismissItem) {
                  onDismissItem(username);
                }
              }}
              label={isMultiple ? `Username @${username}` : 'Username'}
              dismissActionName="remove-username-filter"
            />
          </BaseBadgeWrapper>
        </div>
      </TooltipTrigger>
      <BadgeTooltip
        tooltipTitle="Username Filter"
        label="Username"
        names={isMultiple ? [username] : usernames}
        formatNames={isMultiple ? (names) => `@${names[0]}` : formatUsernames}
      />
    </Tooltip>
  );
};

const UsernameBadge = React.forwardRef<HTMLDivElement, CombinedProps>(({ usernames, onDismissItem, ...props }, ref) => {
  // Create data object to match the pattern
  const data: DemographicData = {
    id: 'usernames',
    names: usernames,
  };

  return (
    <TooltipProvider>
      {usernames.length === 1 ? (
        <SingleUsernameBadge
          username={usernames[0]}
          index={0}
          isMultiple={false}
          data={data}
          usernames={usernames}
          onDismissItem={onDismissItem}
          badgeProps={props}
          forwardedRef={ref}
        />
      ) : (
        usernames.map((username, index) => (
          <SingleUsernameBadge
            key={`${data.id}-${username}-${index}`}
            username={username}
            index={index}
            isMultiple
            data={data}
            usernames={usernames}
            onDismissItem={onDismissItem}
            badgeProps={props}
            forwardedRef={index === 0 ? ref : undefined}
          />
        ))
      )}
    </TooltipProvider>
  );
});

UsernameBadge.displayName = 'UsernameBadge';

export default UsernameBadge;
